'use strict';

// external imports
import React, {
    useCallback,
    useMemo
} from 'react';
import {useSelector} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';

// frontend imports
import {
    InputType,
    inputTypes,
    schemata
} from '~/frontend/src/components/base/XmlImport/Input';
import {
    getToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

// action imports
import {StoreState} from '~/frontend/src/store';


export type DefaultProps = {
    disableType? : boolean;
    disableSchema? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    disableType  : false,
    disableSchema: false
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    type : InputType | '';
    schema : Schema | '';

    onTypeChange : ( type : InputType ) => void;
    onSchemaChange : ( schema : Schema ) => void;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const {
              uid,
              type,
              disableType,
              schema,
              disableSchema,
              onTypeChange,
              onSchemaChange
          }                      = props,
          user                   = useSelector( ( state : StoreState ) => state.session?.user ),
          language               = user ? user.language : undefined,
          onTypeDropdownChange   = useCallback(
              ( event : DropdownChangeEvent ) => {
                  if ( event.value ) {
                      onTypeChange( event.value as InputType );
                  }
              },
              [
                  onTypeChange
              ]
          ),
          onSchemaDropdownChange = useCallback(
              ( event : DropdownChangeEvent ) => {
                  if ( event.value ) {
                      onSchemaChange( event.value as Schema );
                  }
              },
              [
                  onSchemaChange
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            layoutComponents.left.push( {
                type: 'genericNode',

                params: <div key={`${uid}_type`} className="field">
                    <label
                        htmlFor={`${uid}_typeDropdown_i`}
                        className="block"
                    >
                        {__( 'YN.base.xmlImport.input.type.label' )}
                    </label>
                    <Dropdown
                        id={`${uid}_typeDropdown`}
                        inputId={`${uid}_typeDropdown_i`}
                        value={type}
                        onChange={onTypeDropdownChange}
                        options={inputTypes}
                        className="yn-inputgroup"
                        disabled={disableType}
                        style={{ width: '10rem' }}
                    />
                </div>
            } );
            layoutComponents.left.push( {
                type: 'genericNode',

                params: <div key={`${uid}_schema`} className="field ml-3">
                    <label
                        htmlFor={`${uid}_schemaDropdown_i`}
                        className="block"
                    >
                        {__( 'YN.base.xmlImport.input.schema.label' )}
                    </label>
                    <Dropdown
                        id={`${uid}_schemaDropdown`}
                        inputId={`${uid}_schemaDropdown_i`}
                        value={type !== 'authorization' ? schema : undefined}
                        onChange={onSchemaDropdownChange}
                        options={schemata.map( s => s === 'CFG' ? 'CONFIG' : s )}
                        className="yn-inputgroup"
                        disabled={disableSchema}
                        style={{ width: '7rem' }}
                    />
                </div>
            } );

            // right

            const toolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                left={toolbarComponents.left}
                right={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />
        },
        [
            uid,
            type,
            disableType,
            schema,
            disableSchema,
            onTypeDropdownChange,
            onSchemaDropdownChange,
            language
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'XmlImport-Input-Toolbar';

export default Toolbar;
